import React from 'react';
import { AskRequest, AskRequestStatus } from '@hone-automation/common';
import type { PropsType } from './MarkAsSolvedButton';
import { usePatchQueueRequestMutation } from 'hooks/queue';
import { useQueueContext } from '../../Queue/QueueContext';
import { useEvent } from '../../../../hooks/useEvent';
import { useQueryState } from '../../../../hooks/useQueryState';

// Define the expected props for the HOC
export type WithQueueRequestProps = {
  txId: string;
  locationId: string;
  activeTab: AskRequestStatus;
  isSideView?: boolean;
};

const withQueueRequest = <P extends WithQueueRequestProps>(
  WrappedComponent: ({ onSubmit, isPending }: PropsType) => React.JSX.Element
) => {
  return function WithQueueRequest(props: P) {
    const { locationId, txId } = props;
    const [, setRequestId] = useQueryState('requestId');
    const [status] = useQueryState('status');

    const {
      state: { requests, filters },
      dispatch,
    } = useQueueContext();

    const { dispatch: emitEvent } = useEvent('onQueueRequestResolve');

    const { isPending, mutate } = usePatchQueueRequestMutation((trx: any | undefined) => {
      if (!trx) return;

      // Ensure the trx has the correct status
      const updatedTrx = {
        ...trx,
        status: 'Closed',
      } as unknown as AskRequest;

      // Update the request in the context (this will remove it from current tab view)
      dispatch({
        type: 'UPDATE_REQUEST',
        payload: {
          request: updatedTrx,
          status: 'Closed',
        },
      });

      // Show success message
      emitEvent({
        field: filters.status,
        duration: 3000,
        message: 'Request marked as solved.',
      });

      // filter request from queue
      const newRequestsByStatus = requests
        .filter(req => req.status === status && req.id !== trx.id)
        .sort((a, b) => {
          return new Date(a.updatedAt || a.createdAt).getTime() - new Date(b.updatedAt || b.createdAt).getTime();
        });

      // Find the request to be selected (either the one we just updated or a different one based on status)
      const nextTrx = trx.status !== status ? newRequestsByStatus[0] : trx;
      dispatch({ type: 'SET_SELECTED_ITEM', payload: nextTrx as unknown as AskRequest });
      // Update the requestId in the URL
      setRequestId(nextTrx?.id);
    });

    const markAsSolved = async ({ comment }: { comment: string }) => {
      mutate({ txId, status: 'Closed', comment, locationId: locationId! });
    };

    // Extract only the necessary props for the wrapped component
    const wrappedComponentProps = {
      isPending: isPending,
      onSubmit: markAsSolved,
    } as PropsType;

    return <WrappedComponent {...wrappedComponentProps} />;
  };
};

export default withQueueRequest;
